/**
 * Q1 — what contributors actually wrote, and what the AI polish did with it.
 *
 * FOUR NUMBERS AND NO CHART. Adoption, acceptance, median length and thin
 * chapters are read one at a time against a definition; a bar chart of four
 * unrelated statistics would decorate them rather than explain them.
 *
 * ⚠️ A RATE IS NULL AT A ZERO DENOMINATOR AND MUST RENDER '—', NEVER 0%.
 * The server refuses to divide by nothing precisely so this card cannot claim
 * that contributors declined a feature nothing measured. `pct()` below is the
 * only place that decision is expressed, and it never coerces null to zero.
 *
 * ⚠️ THE ADOPTION DENOMINATOR IS ELIGIBILITY, NOT EVERY CHAPTER. AI polish is a
 * Premium-and-up capability carried on the book's options, so a Classic
 * contributor never saw the button. Counting them as having declined it would
 * be the single most misleading thing this card could do — which is why the
 * secondary line names the eligible population rather than the total.
 *
 * The eligibility-blind all-time pair is surfaced only when it DIFFERS from the
 * eligible-scoped one. That gap is a real population — chapters enhanced in
 * books whose couple has since turned AI polish off, or whose tier was
 * downgraded — and it would otherwise vanish behind the denominator that exists
 * to keep the rate honest.
 *
 * Every tooltip is the SERVER's own definition string. Definitions written here
 * would drift from the SQL the moment either changed.
 */

import React from 'react';
import { Box, Card, CardContent, Chip, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { WritingQuality } from '../weddingApi';

interface Props {
  quality?: WritingQuality | null;
  rangeLabel: string;
}

/** A rate the server measured, or '—' when it had no denominator. NEVER 0%. */
function pct(rate: number | null | undefined): string {
  if (rate === null || rate === undefined || !Number.isFinite(rate)) return '—';
  return `${Math.round(rate * 100)}%`;
}

function count(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString();
}

/** One server-shipped definition string, or undefined if this backend has none. */
function defText(definitions: Record<string, unknown> | undefined, key: string): string | undefined {
  const value = definitions ? definitions[key] : undefined;
  return typeof value === 'string' ? value : undefined;
}

interface RowProps {
  label: string;
  value: string;
  secondary: string;
  help?: string;
}

const StatRow: React.FC<RowProps> = ({ label, value, secondary, help }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
    <Box>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Typography variant="body2">{label}</Typography>
        {help && (
          <Tooltip title={help}>
            <InfoOutlinedIcon fontSize="small" color="disabled" />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {secondary}
      </Typography>
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
      {value}
    </Typography>
  </Box>
);

const WritingQualityCard: React.FC<Props> = ({ quality, rangeLabel }) => {
  if (!quality) {
    return (
      <Card sx={{ height: '100%', borderRadius: '16px' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
            Writing quality
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Chapter quality is not reported by this version of the admin API.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const defs = quality.definitions;
  const finishedAllTime = quality.finished.all_time;
  // The gap the eligibility denominator hides: chapters that WERE enhanced in a
  // book whose couple has since turned AI polish off.
  const enhancedGap = quality.enhanced_any_book_all_time - quality.enhanced.all_time;

  return (
    <Card sx={{ height: '100%', borderRadius: '16px' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Writing quality
          </Typography>
          <Chip size="small" color="primary" variant="outlined" label={rangeLabel} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {count(quality.finished.in_range)} chapter
          {quality.finished.in_range === 1 ? '' : 's'} finished in this range ·{' '}
          {count(finishedAllTime)} all time
        </Typography>

        {finishedAllTime === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No contributor has finished a chapter yet.
          </Typography>
        ) : (
          <Stack spacing={1.75}>
            <StatRow
              label="AI polish adoption"
              value={pct(quality.adoption_rate.in_range)}
              secondary={`${pct(quality.adoption_rate.all_time)} all time · ${count(quality.enhanced.all_time)} of ${count(quality.eligible.all_time)} eligible chapters`}
              help={defText(defs, 'adoption_rate') || defText(defs, 'eligible')}
            />
            <StatRow
              label="Kept the polished version"
              value={pct(quality.acceptance_rate.in_range)}
              secondary={`${pct(quality.acceptance_rate.all_time)} all time · ${count(quality.accepted.all_time)} of ${count(quality.enhanced.all_time)} enhanced`}
              help={defText(defs, 'acceptance_rate') || defText(defs, 'accepted')}
            />
            <StatRow
              label="Median chapter length"
              value={
                quality.median_words.in_range === null || quality.median_words.in_range === undefined
                  ? '—'
                  : `${Math.round(quality.median_words.in_range).toLocaleString()} words`
              }
              secondary={
                quality.median_words.all_time === null || quality.median_words.all_time === undefined
                  ? 'No finished chapter to measure, all time'
                  : `${Math.round(quality.median_words.all_time).toLocaleString()} words all time`
              }
              help={defText(defs, 'median_words')}
            />
            <StatRow
              label={`Thin chapters (under ${quality.thin_threshold_words} words)`}
              value={count(quality.thin.in_range)}
              secondary={`${count(quality.thin.all_time)} all time · a follow-up, not an error`}
              help={defText(defs, 'thin')}
            />
          </Stack>
        )}

        {enhancedGap > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            {enhancedGap.toLocaleString()} further chapter{enhancedGap === 1 ? ' was' : 's were'}{' '}
            enhanced in books whose couple has since turned AI polish off, so{' '}
            {quality.enhanced_any_book_all_time.toLocaleString()} chapters have ever been enhanced.
            Those sit outside the eligibility denominator above and are not the numerator of any rate
            here.
          </Typography>
        )}

        {defText(defs, 'finished') && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
            {defText(defs, 'finished')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default WritingQualityCard;
